import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import axios from 'axios';

import API_BASE from '../../utils/api';
const API = API_BASE;

const examTypeLabels = {
  unit_test: 'Unit Test', mid_term: 'Mid Term', quarterly: 'Quarterly',
  half_yearly: 'Half Yearly', annual: 'Annual', assignment: 'Assignment'
};

const Exams = () => {
  const [student, setStudent] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchResults = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/exams/parent/my-child`, { headers });
      setStudent(res.data.student);
      setResults(res.data.results);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const filtered = filterType
    ? results.filter(r => r.examId?.examType === filterType)
    : results;

  // Calculate overall stats
  const totalMarks = filtered.reduce((s, r) => s + (r.examId?.totalMarks || 0), 0);
  const obtainedMarks = filtered.reduce((s, r) => s + (r.marksObtained || 0), 0);
  const overallPct = totalMarks > 0 ? Math.round((obtainedMarks / totalMarks) * 100) : 0;

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">📄 Exam Results & Marksheet</h1>
              <p className="text-gray-500 mt-1">View your child's approved exam results</p>
            </div>
            <ProfileDropdown />
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-500">Loading results...</div>
          ) : !student ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-5xl mb-3">👶</p>
              <p>No child linked to your account</p>
            </div>
          ) : (
            <div>
              {/* Student Info */}
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl p-6 mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-2xl font-bold">
                    {student.name?.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{student.name}</h2>
                    <p className="text-indigo-100">Roll No: {student.rollNumber}</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-4xl font-bold">{overallPct}%</p>
                    <p className="text-indigo-100">Overall Average</p>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">{filtered.length}</p>
                  <p className="text-sm text-blue-600">Total Exams</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {filtered.filter(r => r.marksObtained >= (r.examId?.passingMarks || 0)).length}
                  </p>
                  <p className="text-sm text-green-600">Passed</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-700">
                    {filtered.filter(r => r.marksObtained < (r.examId?.passingMarks || 0)).length}
                  </p>
                  <p className="text-sm text-red-600">Failed</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-700">{obtainedMarks}/{totalMarks}</p>
                  <p className="text-sm text-purple-600">Marks Obtained</p>
                </div>
              </div>

              {/* Filter */}
              <div className="flex gap-2 mb-4 flex-wrap">
                <button onClick={() => setFilterType('')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium ${!filterType ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  All
                </button>
                {Object.entries(examTypeLabels).map(([key, label]) => (
                  <button key={key} onClick={() => setFilterType(key)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium ${filterType === key ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Results Table */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                {filtered.length === 0 ? (
                  <div className="text-center py-10 text-gray-400">
                    <p className="text-4xl mb-2">📭</p>
                    <p>No approved results yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="p-3 text-sm font-semibold text-gray-600">#</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Exam</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Type</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Subject</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Class</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Marks</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Grade</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Result</th>
                          <th className="p-3 text-sm font-semibold text-gray-600">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((r, i) => {
                          const passed = r.marksObtained >= (r.examId?.passingMarks || 0);
                          const pct = r.examId?.totalMarks ? Math.round((r.marksObtained / r.examId.totalMarks) * 100) : 0;
                          return (
                            <tr key={r._id} className={`border-b hover:bg-gray-50 ${!passed ? 'bg-red-50' : ''}`}>
                              <td className="p-3 text-sm">{i + 1}</td>
                              <td className="p-3 font-medium text-gray-800">{r.examId?.examName}</td>
                              <td className="p-3">
                                <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full">
                                  {examTypeLabels[r.examId?.examType]}
                                </span>
                              </td>
                              <td className="p-3 text-sm">{r.examId?.subject}</td>
                              <td className="p-3 text-sm">{r.examId?.classId?.className} - {r.examId?.classId?.section}</td>
                              <td className="p-3 font-bold">
                                {r.marksObtained}/{r.examId?.totalMarks}
                                <span className="text-xs text-gray-400 ml-1">({pct}%)</span>
                              </td>
                              <td className="p-3">
                                <span className={`font-bold text-lg ${
                                  r.grade === 'A+' || r.grade === 'A' ? 'text-green-600' :
                                  r.grade === 'B+' || r.grade === 'B' ? 'text-blue-600' :
                                  r.grade === 'C' ? 'text-yellow-600' :
                                  r.grade === 'D' ? 'text-orange-600' : 'text-red-600'
                                }`}>
                                  {r.grade}
                                </span>
                              </td>
                              <td className="p-3">
                                {passed
                                  ? <span className="text-green-600 font-medium bg-green-100 px-2 py-1 rounded-full text-xs">✅ Pass</span>
                                  : <span className="text-red-600 font-medium bg-red-100 px-2 py-1 rounded-full text-xs">❌ Fail</span>
                                }
                              </td>
                              <td className="p-3 text-sm text-gray-500">{r.remarks || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Exams;
